import { tw } from 'twind/style';

import { ProductColor, Variant } from '__generated__/graphql';
import { useTranslate } from 'hooks/useTranslations';
import { useFeature } from 'hooks/useFeature';
import { ActiveCartPromoProductVariant } from 'types/cart';
import { resizeProductImage } from 'utils/image';

import { TileSizes } from './Sizes';
import { Skeleton } from './Skeleton';

/** If the `HIDE_SOLD_OUT_COLORS_IN_STYLES_SELECTION` feature flag is enabled,
 * only return colors that are orderable and not marked as sold out. */
export const shouldDisplayVariant = (
  variant: Variant | ActiveCartPromoProductVariant,
  hideSoldOutColorsInStylesSelection: boolean
): boolean => {
  if (!hideSoldOutColorsInStylesSelection) return true;

  const { orderable, displayOutOfStock } = variant;
  const soldOut = displayOutOfStock?.soldout;

  // If the variant is orderable and not marked as sold out, show it.
  return Boolean(orderable && !soldOut);
};

const columnSizes = {
  [TileSizes.SMALL]: '3rem',
  [TileSizes.MEDIUM]: '3.6rem',
  [TileSizes.LARGE]: '4.6rem',
};

export const Styles: React.FC<{
  variations: Array<Variant | ActiveCartPromoProductVariant>;
  desiredSwatch?: string;
  onChange?: (color: string) => void;
  isHydrating?: boolean;
  colors?: ProductColor[];
}> = ({ variations = [], desiredSwatch, onChange, isHydrating, colors }) => {
  const t = useTranslate();
  const hideSoldOutColorsInStylesSelection = useFeature(
    'HIDE_SOLD_OUT_COLORS_IN_STYLES_SELECTION'
  );

  const sizePickerStyle = {
    gridTemplateColumns: `repeat(auto-fill, minmax(${
      columnSizes[TileSizes.MEDIUM]
    }, 1fr))`,
  };

  if (isHydrating && colors?.length) {
    return (
      <div className="flex flex-col gap-6">
        <div className="grid gap-1" style={sizePickerStyle}>
          {[...Array(colors.length)].map((_, i) => (
            <Skeleton
              key={i}
              className={tw('w-full aspect-1-1')}
              delay={`${(i + 1) * 100}ms`}
            />
          ))}
        </div>
      </div>
    );
  }

  const variationsToShow = variations.filter(variant =>
    shouldDisplayVariant(variant, hideSoldOutColorsInStylesSelection)
  );

  return (
    <div
      id="style-picker"
      aria-label={t('styles')}
      role="radiogroup"
      className="grid gap-1"
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(3.6rem, 1fr))' }}
    >
      {variationsToShow.map(variation => (
        <label
          key={variation.id}
          data-test-id="color"
          data-color={variation.colorValue}
          className={tw(
            'relative flex items-center justify-center flex-none rounded-sm',
            desiredSwatch === variation.colorValue &&
              'border border-neutral-70',
            (variation.displayOutOfStock || variation.orderable === false) &&
              'bg-puma-black-700'
          )}
        >
          <span className="sr-only">
            {variation.colorName}
            {variation.displayOutOfStock || variation.orderable === false
              ? `: ${t('outOfStock')}`
              : null}
          </span>
          <img
            aria-hidden
            src={resizeProductImage(variation.preview, 100)}
            alt={variation.colorName || ''}
            loading="lazy"
            width="120"
            height="120"
            className={tw(
              'w-full',
              (variation.displayOutOfStock || variation.orderable === false) &&
                'opacity-50'
            )}
          />
          <input
            type="radio"
            name="swatch"
            checked={desiredSwatch === variation.colorValue}
            className="absolute appearance-none block w-full h-full inset-0 cursor-pointer"
            tabIndex={0}
            onChange={e =>
              onChange && e.target.checked && onChange(variation.colorValue)
            }
          />
        </label>
      ))}
    </div>
  );
};
